import { FilterType, TDataTablePager } from '@monetize/types/common';
import { useCallback, useMemo } from 'react';
import { ExtraActiveFilter } from './DataTableActiveFilters';

export interface ExternalFilterDef {
  key: string;
  columnHeader: string;
  /**
   * Return chip label/tooltip for a given filter. Return null/undefined to
   * suppress the chip even when the filter has a value.
   */
  getChipProps?: (
    filter: FilterType,
  ) => { label?: string; tooltipLabel?: string } | null | undefined;
  /**
   * Custom check for whether the filter value should produce a chip.
   * Defaults to `!!filter.value && (!Array.isArray(filter.value) || filter.value.length > 0)`.
   */
  isActive?: (filter: FilterType) => boolean;
}

interface UseExternalFiltersOptions {
  filters: FilterType[] | null | undefined;
  setFilters: (filters: FilterType[]) => void;
  pager: TDataTablePager;
  setPager: (pager: TDataTablePager) => void;
  filterDefs: ExternalFilterDef[];
}

interface UseExternalFiltersResult {
  /** Pass as `initialFilters` to DataTable — external keys stripped */
  initialFilters: FilterType[];
  /** Pass as `onFiltersChange` to DataTable — re-merges external filters */
  onFiltersChange: (newFilters: FilterType[]) => void;
  /** Pass as `extraActiveFilters` to DataTable */
  extraActiveFilters: ExtraActiveFilter[];
  externalFilters: FilterType[];
  setExternalFilters: (updatedExternalFilters: FilterType[]) => void;
}

function defaultIsActive(filter: FilterType): boolean {
  const { value } = filter;
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return !!value;
}

export function useExternalFilters({
  filters,
  setFilters,
  pager,
  setPager,
  filterDefs,
}: UseExternalFiltersOptions): UseExternalFiltersResult {
  const externalFilterKeys = useMemo(
    () => new Set(filterDefs.map((filterDef) => filterDef.key)),
    [filterDefs],
  );

  const initialFilters = useMemo(
    () =>
      (filters ?? []).filter((filter) => !externalFilterKeys.has(filter.key)),
    [filters, externalFilterKeys],
  );

  const externalFilters = useMemo(
    () =>
      (filters ?? []).filter((filter) => externalFilterKeys.has(filter.key)),
    [filters, externalFilterKeys],
  );

  const onFiltersChange = useCallback(
    (newFilters: FilterType[]) => {
      setFilters([...externalFilters, ...newFilters]);
    },
    [externalFilters, setFilters],
  );

  const setExternalFilters = useCallback(
    (updatedExternalFilters: FilterType[]) => {
      const nonExternalFilters = (filters ?? []).filter(
        (filter) => !externalFilterKeys.has(filter.key),
      );
      setFilters([...nonExternalFilters, ...updatedExternalFilters]);
      setPager({ ...pager, page: 0, first: 0 });
    },
    [filters, externalFilterKeys, setFilters, pager, setPager],
  );

  const handleRemoveFilter = useCallback(
    (key: string) => {
      setFilters((filters ?? []).filter((filter) => filter.key !== key));
      setPager({ ...pager, page: 0, first: 0 });
    },
    [filters, setFilters, pager, setPager],
  );

  const extraActiveFilters = useMemo<ExtraActiveFilter[]>(() => {
    const chips: ExtraActiveFilter[] = [];

    for (const filterDef of filterDefs) {
      const filter = (filters ?? []).find(
        (activeFilter) => activeFilter.key === filterDef.key,
      );
      if (!filter) {
        continue;
      }

      const isActive = filterDef.isActive
        ? filterDef.isActive(filter)
        : defaultIsActive(filter);
      if (!isActive) {
        continue;
      }

      const chipProps = filterDef.getChipProps
        ? filterDef.getChipProps(filter)
        : {};
      if (chipProps === null || chipProps === undefined) {
        continue;
      }

      chips.push({
        key: filterDef.key,
        columnHeader: filterDef.columnHeader,
        label: chipProps.label,
        tooltipLabel: chipProps.tooltipLabel,
        onRemove: () => handleRemoveFilter(filterDef.key),
      });
    }

    return chips;
  }, [filters, filterDefs, handleRemoveFilter]);

  return {
    initialFilters,
    onFiltersChange,
    extraActiveFilters,
    externalFilters,
    setExternalFilters,
  };
}
